import React, { useState, useCallback } from "react";
import { Container, Title, Text, TextInput, LoadingOverlay, Alert, Button, ActionIcon, Group, Space } from '@mantine/core';
import { IconPlus, IconPencil, IconTrash, IconDownload } from "@tabler/icons";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-location";
import { CSVLink } from "react-csv";
import * as App from "./../model/App";
import { PatentEditModal, PatentsTable, PatentsTableColumns } from "./../components";
import { useDeleteConfirmation } from "./../hooks";

const RESULTS_PER_PAGE = 20;

export default function PatentsScreen() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Load patents...
    const { isLoading, isError, data } = useQuery(["patents"], async () => { 
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Read.getPatents(userID); 
    });

    // Load clients...
    const { isLoading: isLoadingClients, isError: isErrorClients, data: clients } = useQuery(["clients"], async () => { 
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Read.getClients(userID); 
    });

    const [isShowingEditModal, setIsShowingEditModal] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");

    const createPatent = useMutation(async (title: string) => { 
            const patent: App.Data.App.IPatent = App.Data.App.PatentUtils.getDefault(title);
            const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
            return App.Firebase.Write.createPatent(userID, patent); 
        }, { onSuccess: () => { queryClient.invalidateQueries(["patents"]); } }
    );

    const deletePatent = useMutation(async (key: string) => {
            const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
            return App.Firebase.Write.deletePatent(userID, key);
        }, { onSuccess: async () => { queryClient.invalidateQueries(["patents"]); } }
    );

    // Delete confirmation...
    const { setShowDeleteConfirmation, deleteConfirmationDialog } = useDeleteConfirmation({ 
        title: "Delete",
        message: "Are you sure you want to delete this patent? This action can NOT be undone.",
        onDeleteConfirm: deletePatent.mutate
    });

    const onEditPatentClick = useCallback((patentId: string) => {
        navigate({ to: `/patent/${patentId}` });
    }, [navigate]);

    // Convert data to a CSV-displayable format...
    const getCsvData = useCallback(() => {
        if (!data) { return []; }

        const patents = Object.keys(data)
        // Link client to the data (so we can search by it)...
        .map((key) => { 
            const patent = data[key];
            const client = (patent.clientId && clients && clients[patent.clientId]) ? clients[patent.clientId] : null;
            return { 
                key: key, 
                client: client,
                patent: patent
            };
        })

        // Filter based on search text...
        .filter((p) => {
            if (searchText.length === 0) { return true; }
            if (p.client && p.client.agent.toLowerCase().includes(searchText.toLowerCase())) { return true; }
            if (App.Data.App.PatentUtils.searchMatchesPatent(searchText, p.patent)) { return true; }
            return false;
        });

        return App.Data.App.PatentUtils.toCsvFormat(patents.map(x => x.patent), clients ?? {});
    }, [data, searchText]);


    // Build data for the table...
    const tmData = React.useMemo((): PatentsTableColumns[] => {
        if (!data) { return []; }

        const patents = Object.keys(data)
            // Link client to the data (so we can search by it)...
            .map((key) => { 
                const patent = data[key];
                const client = (patent.clientId && clients && clients[patent.clientId]) ? clients[patent.clientId] : null;
                return { 
                    key: key, 
                    client: client,
                    patent: patent
                };
            })

            // Filter based on search text...
            .filter((p) => {
                if (searchText.length === 0) { return true; }
                if (p.client && p.client.agent.toLowerCase().includes(searchText.toLowerCase())) { return true; }
                if (App.Data.App.PatentUtils.searchMatchesPatent(searchText, p.patent)) { return true; }
                return false;
            });

        return patents.map(p => {
            const key = p.key;
            const patent: App.Data.App.IPatent = p.patent;
            return {
                edit: <ActionIcon onClick={() => { onEditPatentClick(key) }}><IconPencil /></ActionIcon>,
                delete: <ActionIcon onClick={() => { setShowDeleteConfirmation(true, key); }}><IconTrash /></ActionIcon>,
                title: patent.title,
                grantingNumber: patent.grantingNumber,
                applicationNumber: patent.applicationNumber,
                owner: (patent.owners) ? patent.owners.join(", ") : "",
                client: p.client?.agent || "",
                referenceNumber: patent.referenceNumber
            };
        });
    }, [data, searchText, onEditPatentClick, setShowDeleteConfirmation]);

    return (
        <Container ml={0} mr={30} style={{ maxWidth: "100%" }}>
            <Group position="apart">
                <Title>Patents</Title>
                <Button leftIcon={<IconPlus size={14} />} onClick={() => { setIsShowingEditModal(true); }}>New Patent</Button>
            </Group>
            
            <LoadingOverlay visible={isLoading || createPatent.isLoading} />
            {isError && <Alert color="red" title="Error">Error loading data</Alert>}
            {createPatent.isError && <Alert color="red" title="Error">Error creating patent</Alert>}
            {deleteConfirmationDialog}

            <PatentEditModal visible={isShowingEditModal} onClose={() => { setIsShowingEditModal(false); }} onSave={(title: string) => {
                createPatent.mutate(title);
                setIsShowingEditModal(false);
            }} />            

            <Space h="md" />
            {data && Object.keys(data).length === 0 && <Text>No patents found</Text>}
            {data && Object.keys(data).length > 0 && <>
                <Group position="right">
                    <CSVLink data={getCsvData()} filename="aestrada_patents">
                        <Button leftIcon={<IconDownload size={14} />} variant="subtle">Download csv</Button>
                    </CSVLink>
                    <TextInput placeholder="Search" value={searchText} onChange={(event) => { setSearchText(event.target.value); }}  />
                </Group>
                <PatentsTable data={tmData} pageSize={RESULTS_PER_PAGE} />
                </>
            }
        </Container>
    );
}